/* eslint-disable max-lines */
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { AppConstants } from 'common/app-constants';
import { Option } from 'common/components/select/select.component';
import { AccountStatusModel } from 'common/models/account-status';
import { ToastService } from 'common/services/toast.service';
import { GetUserStatus } from 'common/store/account-status/account-status.actions';
import { AccountStatusState } from 'common/store/account-status/account-status.state';
import { createAccountEmailInputValidator, createAccountInputValidator, getErrorMessage } from 'common/utils/createAccountValidators';
import { EmailVerification } from 'private/app/components/email-validation/email-validation.component';
import { PlaceHolderText } from 'private/app/models/account-admin-company.model';
import { ProfileDetails } from 'private/app/models/user-validation';
import { CountryForMasking } from 'private/app/services/account-admin/account-admin-company-create.service';
import { AccountAdminCompanyUtilsService } from 'private/app/services/account-admin/account-admin-company-utils.service';
import { inputValidator } from 'private/app/services/account-admin/account-admin-edit-form.service';
import { UserAdminService } from 'private/app/services/account-admin/user/user-admin.service';
import { CommonUtilsService } from 'private/app/services/common-utils.service';
import { UserValidationService } from 'private/app/services/user-validation/user-validation.service';
import { BehaviorSubject, combineLatest, forkJoin, Observable, Subject, Subscription } from 'rxjs';
import { map, mergeMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'hvac-user-validation-form',
    templateUrl: './user-validation-form.component.html',
    styleUrls: ['./user-validation-form.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class UserValidationFormComponent implements OnInit {
    @Select(AccountStatusState.getProfileInfo) userInfo$: Observable<AccountStatusModel>;
    @Output() onValidChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() emailVaidationCompleted: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() requestUserProfileUpdate: Observable<boolean> = new Observable<boolean>();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    firstNameErrorMessage$: Observable<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    lastNameErrorMessage$: Observable<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    zipCodeErrorMessage$: Observable<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addressErrorMessage$: Observable<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cityErrorMessage$: Observable<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    countryErrorMessage$: Observable<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    stateErrorMessage$: Observable<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    emailErrorMessage$: Observable<any>;

    countryOptionData$: BehaviorSubject<Option[]> = new BehaviorSubject<Option[]>([]);
    stateOptionData: BehaviorSubject<Option[]> = new BehaviorSubject<Option[]>([]);
    enableEmailVerification$ = new BehaviorSubject<boolean>(false);
    userEmailId$ = new BehaviorSubject<string>('');

    emailVerificationStatus = new BehaviorSubject<EmailVerification>({
        email: '',
        status: false
    });

    placeHolderByCountry$: BehaviorSubject<PlaceHolderText> = new BehaviorSubject<PlaceHolderText>({
        placeHolderPhone: '',
        placeHolderOffice: '',
        placeHolderMobile: '',
        placeHolderFax: '',
        placeHolderTollFree: '',
        placeHolderZipcode: { label: '' }
    });

    public userInfoForm = new UntypedFormGroup({
        userIdControl: new UntypedFormControl(''),
        myHvacPinControl: new UntypedFormControl(''),
        employeeIdControl: new UntypedFormControl(''),
        employeeNameControl: new UntypedFormControl(''),
        usernameControl: new UntypedFormControl(''),
        firstNameControl: new UntypedFormControl('', [createAccountInputValidator(41, true)]),
        lastNameControl: new UntypedFormControl('', [createAccountInputValidator(41, true)]),
        address1Control: new UntypedFormControl('', [createAccountInputValidator(61, true)]),
        address2Control: new UntypedFormControl('', [createAccountInputValidator(61, false)]),
        cityControl: new UntypedFormControl('', [createAccountInputValidator(41, true)]),
        countryControl: new UntypedFormControl('', [Validators.required]),
        stateControl: new UntypedFormControl('', [Validators.required]),
        zipControl: new UntypedFormControl('', [Validators.required]),
        phoneControl: new UntypedFormControl('', [inputValidator(21, false), Validators.pattern(AppConstants.validPhoneRegex)]),
        cellControl: new UntypedFormControl('', [inputValidator(21, false), Validators.pattern(AppConstants.validPhoneRegex)]),
        emailControl: new UntypedFormControl('',
            [createAccountEmailInputValidator(242, true, AppConstants.validEmailRegex)]),
        faxControl: new UntypedFormControl('', [inputValidator(21, false), Validators.pattern(AppConstants.validPhoneRegex)]),
        termsControl: new UntypedFormControl(false, Validators.requiredTrue)
    });

    public maskPhoneNumber: boolean = false;
    private userProfile$: BehaviorSubject<ProfileDetails[]> = new BehaviorSubject<ProfileDetails[]>([]);
    private userData: AccountStatusModel;
    private ngOnDestroy$ = new Subject();
    private eventsSubscription: Subscription;
    private errorMessages$ = this.translate.get('USER_VALIDATION.ERROR_MESSAGES');

    constructor(
        private store: Store,
        private translate: TranslateService,
        private userValidationService: UserValidationService,
        private toastService: ToastService,
        private commonUtils: CommonUtilsService,
        public userAdminService: UserAdminService,
        public accountAdminCompanyUtilsService: AccountAdminCompanyUtilsService
    ) { }

    ngOnInit() {
        this.eventsSubscription = this.requestUserProfileUpdate.subscribe((formStatus) => {
            if (formStatus) {
                this.updateUserProfile();
            }
        });
        this.userInfoForm.statusChanges.subscribe((status) => {
            this.onValidChange.emit(status === 'VALID');
        });

        this.commonUtils.getCountryOptions().subscribe((countryList) => {
            this.countryOptionData$.next(countryList);
        });

        this.userInfo$.pipe(
            takeUntil(this.ngOnDestroy$),
            map((statusData) => {
                this.userData = statusData;

                return this.userData;
            }),
            mergeMap((userData) => forkJoin([this.userValidationService.getProfileDetailsV1(userData?.userId)]))
        ).subscribe((profileInfo) => {
            profileInfo[0].id = this.userData.company.id;
            this.userEmailId$.next(profileInfo[0].email);
            this.setCountrySelection(profileInfo);
            this.userProfile$.next(profileInfo);
            this.userValidationService.setUserDetails(this.userInfoForm, profileInfo);
        });


        combineLatest([
            this.userProfile$,
            this.countryOptionData$
        ]).pipe(
            takeUntil(this.ngOnDestroy$),
            map(([profileInfo, countryOptionData$]) => {
                if (profileInfo.length > 0 && countryOptionData$.length > 0) {
                    return this.setCountrySelection(profileInfo);
                }
            })
        ).subscribe();

        combineLatest([this.userProfile$, this.countryOptionData$, this.stateOptionData]).pipe(
            takeUntil(this.ngOnDestroy$),
            map(([profileInfo, countryOptionData$, stateOptionData]) => {
                if (profileInfo.length > 0 && countryOptionData$.length > 0 && stateOptionData.length > 0) {
                    this.setStateSelection(profileInfo, stateOptionData);
                    this.userInfoForm.markAllAsTouched();
                }
            })
        ).subscribe();

        this.firstNameErrorMessage$ = combineLatest([
            this.userInfoForm.controls.firstNameControl.statusChanges, this.errorMessages$
        ]).pipe(
            takeUntil(this.ngOnDestroy$),
            map(([valid, message]) => {
                if (valid) {
                    this.userProfile$.value[0].firstName = this.userInfoForm.controls.firstNameControl.value;
                }
                getErrorMessage(this.userInfoForm.controls.firstNameControl.errors, message);
            })
        );

        this.lastNameErrorMessage$ = combineLatest([this.userInfoForm.controls.lastNameControl.statusChanges, this.errorMessages$]).pipe(
            takeUntil(this.ngOnDestroy$),
            map(([valid, message]) => {
                if (valid) {
                    this.userProfile$.value[0].lastName = this.userInfoForm.controls.lastNameControl.value;
                }
                getErrorMessage(this.userInfoForm.controls.lastNameControl.errors, message);
            })
        );

        this.addressErrorMessage$ = combineLatest([this.userInfoForm.controls.address1Control.statusChanges, this.errorMessages$]).pipe(
            takeUntil(this.ngOnDestroy$),
            map(([valid, message]) => {
                if (valid) {
                    this.userProfile$.value[0].userAddress1 = this.userInfoForm.controls.address1Control.value;
                }
                getErrorMessage(this.userInfoForm.controls.address1Control.errors, message);
            })
        );

        this.cityErrorMessage$ = combineLatest([this.userInfoForm.controls.cityControl.statusChanges, this.errorMessages$]).pipe(
            takeUntil(this.ngOnDestroy$),
            map(([valid, message]) => {
                if (valid) {
                    this.userProfile$.value[0].userCity = this.userInfoForm.controls.cityControl.value;
                }
                getErrorMessage(this.userInfoForm.controls.cityControl.errors, message);
            })
        );

        this.stateErrorMessage$ = combineLatest([this.userInfoForm.controls.stateControl.statusChanges, this.errorMessages$]).pipe(
            takeUntil(this.ngOnDestroy$),
            map(([valid, message]) => {
                if (valid) {
                    this.userProfile$.value[0].userState = this.userInfoForm.controls.stateControl.value;
                }
                getErrorMessage(this.userInfoForm.controls.stateControl.errors, message);
            })
        );

        this.countryErrorMessage$ = combineLatest([this.userInfoForm.controls.countryControl.statusChanges, this.errorMessages$]).pipe(
            takeUntil(this.ngOnDestroy$),
            map(([valid, message]) => {
                if (valid) {
                    this.userProfile$.value[0].userCountry = this.userInfoForm.controls.countryControl.value;
                }
                getErrorMessage(this.userInfoForm.controls.countryControl.errors, message);
            })
        );
        this.zipCodeErrorMessage$ = combineLatest([this.userInfoForm.controls.zipControl.statusChanges, this.errorMessages$]).pipe(
            takeUntil(this.ngOnDestroy$),
            map(([valid, message]) => {
                if (valid === 'VALID' && this.userProfile$.value[0]) {
                    this.userProfile$.value[0].userPostalCode = this.userInfoForm.controls.zipControl.value;
                }
                getErrorMessage(this.userInfoForm.controls.zipControl.errors, message);
            })
        );
        this.emailErrorMessage$ = combineLatest([this.userInfoForm.controls.emailControl.statusChanges, this.errorMessages$]).pipe(
            takeUntil(this.ngOnDestroy$),
            map(([valid, message]) => {
                if (valid) {
                    this.userProfile$.value[0].email = this.userInfoForm.controls.emailControl.value;
                }
                getErrorMessage(this.userInfoForm.controls.emailControl.errors, message);
            })
        );

        combineLatest([
            this.userProfile$,
            this.countryOptionData$
        ]).subscribe(([profileInfo]) => {
            if (!profileInfo?.length) {
                return;
            }

            this.setCountrySelection(profileInfo);

            setTimeout(() => this.formatPhonesSafely());
        });
        this.userInfoForm.controls.address2Control.statusChanges.subscribe((valid) => {
            if (valid) {
                this.userProfile$.value[0].userAddress2 = this.userInfoForm.controls.address2Control.value;
            }
        });

        this.accountAdminCompanyUtilsService.placeHolderData$.subscribe((placeHolderData) => {
            this.placeHolderByCountry$.next(placeHolderData);
        });
        this.userInfoForm.controls.countryControl.valueChanges.subscribe((selectedCountry) => {
            if (selectedCountry) {
                this.stateOptionData.next([]);
                this.userInfoForm.controls.stateControl.setValue('');
                if (Array.isArray(selectedCountry)) {
                    this.maskPhoneNumber = Boolean(selectedCountry[0].value === CountryForMasking.US || selectedCountry[0].value === CountryForMasking.CA);
                    this.fetchStateListByCountry(selectedCountry[0].value);
                    this.accountAdminCompanyUtilsService.formInputPlaceHolder(selectedCountry[0].value);
                    this.userValidationService.updatePostalCodeValidator(this.userInfoForm, selectedCountry[0].value, 'zipControl');
                    this.commonUtils.updatePhoneValidators(this.userInfoForm, selectedCountry[0].value, ['phoneControl', 'cellControl', 'faxControl']);
                }
                else {
                    const countrySelection = this.countryOptionData$.value.find((country) => country.name === selectedCountry);
                    this.maskPhoneNumber = countrySelection?.value === CountryForMasking.US || countrySelection?.value === CountryForMasking.CA;
                    if (countrySelection) {
                        this.fetchStateListByCountry(countrySelection.value);
                        this.accountAdminCompanyUtilsService.formInputPlaceHolder(countrySelection.value);
                        this.userValidationService.updatePostalCodeValidator(this.userInfoForm, countrySelection.value, 'zipControl');
                        this.commonUtils.updatePhoneValidators(this.userInfoForm, countrySelection.value, ['phoneControl', 'cellControl', 'faxControl']);
                    }
                }
            }
        });

        this.userInfoForm.controls.stateControl.valueChanges.subscribe((selectedState) => {
            if (selectedState && this.userProfile$.value[0]) {
                if (Array.isArray(selectedState)) {
                    this.userProfile$.value[0].userState = selectedState[0].value;
                }
                else {
                    const stateSelection = this.stateOptionData.value.find((state) => state.name === selectedState);
                    if (stateSelection) {
                        this.userProfile$.value[0].userState = stateSelection.value;
                    }
                }
            }
        });
    }

    cleanPhone = (val?: string) => {
        if (!val) {
            return '';
        }
        const digits = val.replace(/\D/g, '');
        if (this.getSelectedCountryCode() === 'US' || this.getSelectedCountryCode() === 'CA') {
            return digits.slice(0, 10);
        }

        return digits;
    };

    updateUserProfile() {
        if (!(this.userEmailId$.value.toLowerCase() === this.userInfoForm.controls.emailControl.value.toLowerCase()) && this.emailVerificationStatus.value.email !== this.userInfoForm.controls.emailControl.value.toLowerCase()) {
            this.enableEmailVerification$.next(true);

            return;
        }
        this.userProfile$.value[0].isValidationRequired = false;
        const payload = {
            ...this.userProfile$.value[0],
            firstName: this.userProfile$.value[0].firstName?.trim(),
            lastName: this.userProfile$.value[0].lastName?.trim(),
            userAddress1: this.userProfile$.value[0].userAddress1?.trim(),
            userCity: this.userProfile$.value[0].userCity?.trim(),
            userCountry: this.getSelectedCountryCode(),
            userPostalCode: this.userInfoForm.controls.zipControl.value,
            userPhone: this.cleanPhone(this.userInfoForm.controls.phoneControl.value),
            userCellPhone: this.cleanPhone(this.userInfoForm.controls.cellControl.value),
            userFax: this.cleanPhone(this.userInfoForm.controls.faxControl.value)
        };
        this.userValidationService.updateUserProfileDetailsV1(payload).subscribe((data) => {
            if (data.status === 'Success') {
                this.toastService.add({
                    content: this.translate.instant('USER_VALIDATION.PROFILE_UPDATE_SUCCESS'),
                    id: 'USER_VALIDATION.PROFILE_UPDATE_SUCCESS',
                    theme: 'success',
                    closeable: true,
                    autoClose: true
                });
                this.enableEmailVerification$.next(false);
                this.store.dispatch(new GetUserStatus());
            }
        });
    }

    formatPhonesSafely() {
        const countryCode = this.getSelectedCountryCode();
        if (!countryCode) {
            return;
        }

        ['phoneControl', 'cellControl', 'faxControl'].forEach((controlName) => {
            const control = this.userInfoForm.controls[controlName];
            const value = control?.value;
            if (!value || typeof value !== 'string') {
                return;
            }
            const formatted = this.commonUtils.formatPhoneNumber(value, countryCode);
            control.setValue(formatted, { emitEvent: false });
        });
    }

    onPhoneInput(event: Event, controlName: string) {
        const input = event.target as HTMLInputElement;
        const countryCode = this.getSelectedCountryCode();

        let value = input.value;

        if (countryCode === 'US' || countryCode === 'CA') {
            value = this.commonUtils.formatPhoneNumber(value, countryCode);
        }
        else {
            value = value.replace(/[^0-9+\s]/g, '');
        }

        this.userInfoForm.controls[controlName].setValue(value);
    }

    setCountrySelection(profileInfo: ProfileDetails[]) {
        const countrySelection = this.countryOptionData$.value.find((country) => country.value === profileInfo[0].userCountry);
        if (countrySelection) {
            this.userInfoForm.controls.countryControl.setValue(countrySelection.name);
        }
    }

    setStateSelection(profileInfo: ProfileDetails[], stateOptionData: Option[]) {
        const stateSelection = stateOptionData.find((country) => country.value === profileInfo[0].userState);
        if (stateSelection) {
            this.userInfoForm.controls.stateControl.setValue(stateSelection.name);
        }
    }

    fetchStateListByCountry(selectedCountry: string) {
        this.commonUtils.getStateByCountry(selectedCountry).subscribe((stateList) => {
            this.stateOptionData.next(stateList);
        });
    }

    emailVerification(emailVerification: EmailVerification) {
        if (emailVerification.status) {
            this.emailVerificationStatus.next(emailVerification);
            this.enableEmailVerification$.next(false);
            this.emailVaidationCompleted.next(true);
            this.updateUserProfile();
        }
    }

    cancelValidation(value: boolean) {
        this.enableEmailVerification$.next(value);
    }

    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
        this.eventsSubscription.unsubscribe();
    }

    changeTermsControl($event: Event) {
        return this.userInfoForm.controls.termsControl.setValue(($event.target as HTMLInputElement).checked);
    }


    onPostalCodeInput(event: Event) {
        const input = event.target as HTMLInputElement;
        if (!this.getSelectedCountryCode()) {
            return;
        }
        const formattedValue = this.commonUtils.formatPostalCode(input.value, this.getSelectedCountryCode());
        input.value = formattedValue;
        this.userInfoForm.controls.zipControl.setValue(formattedValue);
    }

    getSelectedCountryCode(): string {
        const selectedCountry = this.userInfoForm.controls.countryControl.value;
        if (Array.isArray(selectedCountry)) {
            return selectedCountry[0]?.value || '';
        }
        const countrySelection = this.countryOptionData$.value.find((country) => country.name === selectedCountry);

        return countrySelection?.value || '';
    }
}

